import { type FC, useMemo, useState } from "react"
import { Link } from "react-router"
import styled from "styled-components"

import { parseIntNumber } from "@forento/shared/utilities/number"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { type Downloadable, useDownloadables } from "~/api/downloadable"
import useQueryParam from "~/hooks/useQueryParam"
import Button from "~/themes/school/components/Button"
import Card, { CardText, CardThumbnail, CardThumbnailSticker, CardTitle, Cards } from "~/themes/school/components/Card"
import ContentSearch from "~/themes/school/components/ContentSearch"
import Layout from "~/themes/school/components/Layout"
import PageHeader, { PageTitle } from "~/themes/school/components/PageHeader"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import { fonts, lightTextColor } from "~/themes/school/styles"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"

const DownloadablesPage: FC = () => {
	const t = useTranslation()
	const downloadables = useDownloadables()
	const selectedTag = useQueryParam("tag")

	const [searchItems, setSearchItems] = useState<number[]>()

	const keys = useMemo(() => ["name" as const], [])

	return (
		<Layout>
			<PageHeader>
				<PageTitle>{t("downloadable.title")}</PageTitle>
				<ContentSearch
					keys={keys}
					items={downloadables.status === "success" ? downloadables.data : []}
					onItemsToShowChange={setSearchItems}
				/>
			</PageHeader>
			{downloadables.status === "error" ? (
				<p>{t("failedToLoad")}</p>
			) : downloadables.status === "loading" ? (
				<PartialLoadingPage />
			) : downloadables.status === "success" ? (
				<>
					{downloadables.tags.length > 0 && (
						<Tags>
							{downloadables.tags.map(tag => (
								<Tag
									key={tag.id}
									$isActive={selectedTag.value === tag.id.toString()}
									onClick={() => selectedTag.set(tag.id.toString())}
								>
									{tag.label}
								</Tag>
							))}
							{selectedTag.value !== null && (
								<Tag $isActive onClick={() => selectedTag.set(null)}>
									{t("clearFilter")}
								</Tag>
							)}
						</Tags>
					)}
					<Downloadables
						downloadables={downloadables.data}
						selectedTag={parseIntNumber(selectedTag.value)}
						searchItems={searchItems}
					/>
				</>
			) : (
				exhaustiveGuard(downloadables.status)
			)}
		</Layout>
	)
}

type DownloadablesProps = {
	downloadables: Downloadable[]
	selectedTag: number | null
	searchItems: number[] | undefined
}
const Downloadables: FC<DownloadablesProps> = ({ downloadables, selectedTag, searchItems }) => {
	let filteredDownloadables = downloadables
	if (selectedTag !== null) {
		filteredDownloadables = filteredDownloadables.filter(downloadable => downloadable.tagIds.includes(selectedTag))
	}
	if (searchItems !== undefined) {
		filteredDownloadables = filteredDownloadables
			.filter(downloadable => searchItems.includes(downloadable.id))
			.sort((a, b) => (searchItems ? searchItems.indexOf(b.id) - searchItems.indexOf(a.id) : 0))
	}
	return (
		<Cards>
			{filteredDownloadables.map(downloadable => (
				<Card key={downloadable.id} as={Link} to={routes.downloadable.detail(downloadable.id)}>
					<CardTitle>{downloadable.name}</CardTitle>
					<CardThumbnail
						filePath={downloadable.thumbnailUrl}
						sticker={<CardThumbnailSticker>{downloadable.priceLabel.value}</CardThumbnailSticker>}
					/>
					{downloadable.shortDescription && <CardText>{downloadable.shortDescription}</CardText>}
				</Card>
			))}
		</Cards>
	)
}

const Tags = styled.div`
	width: 100%;
	display: flex;
	gap: 22px;
	flex-wrap: wrap;
	margin-bottom: 24px;
`

const Tag = styled(Button)<{ $isActive: boolean }>`
	font-family: ${fonts.quicksand};
	font-size: 14px;
	font-weight: bold;
	color: ${props => (props.$isActive ? props.theme.primaryColor : lightTextColor)};
`

export default DownloadablesPage
